import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, vModelCheckbox as _vModelCheckbox, withDirectives as _withDirectives, vModelSelect as _vModelSelect } from "vue"

const _hoisted_1 = { class: "mt-10 bg-white border border-gray-200 rounded-xl shadow-sm" }
const _hoisted_2 = {
  key: 0,
  class: "flex justify-center py-10"
}
const _hoisted_3 = {
  key: 1,
  class: "p-5 space-y-6"
}
const _hoisted_4 = { class: "divide-y divide-gray-100 border border-gray-200 rounded-lg" }
const _hoisted_5 = { class: "text-sm text-gray-700" }
const _hoisted_6 = { class: "flex items-center gap-2 shrink-0" }
const _hoisted_7 = ["onUpdate:modelValue"]
const _hoisted_8 = {
  key: 0,
  class: "space-y-2 mb-3"
}
const _hoisted_9 = { class: "text-sm text-gray-700 flex-1 min-w-0 truncate" }
const _hoisted_10 = { class: "text-xs text-gray-400 mr-1" }
const _hoisted_11 = { class: "flex items-center gap-2 text-xs text-gray-600 shrink-0" }
const _hoisted_12 = ["onUpdate:modelValue"]
const _hoisted_13 = ["onClick"]
const _hoisted_14 = {
  key: 1,
  class: "text-xs text-gray-400 italic mb-3"
}
const _hoisted_15 = { class: "flex flex-col sm:flex-row gap-2" }
const _hoisted_16 = { label: "Ambassades" }
const _hoisted_17 = ["value"]
const _hoisted_18 = { label: "Directions" }
const _hoisted_19 = ["value"]
const _hoisted_20 = ["disabled"]
const _hoisted_21 = { class: "flex justify-end" }
const _hoisted_22 = ["disabled"]

import { ref, reactive, computed, onMounted } from 'vue'
import { apiFetch } from '@/utils/apiFetch'
import { useToast } from '@/utils/toast'

interface Entity { id: number; nom: string }
interface ApiException {
  portee: string
  ambassade_id: number | null
  direction_id: number | null
  nom: string
  voit_globaux: boolean
}
interface ApiPayload {
  defaults: Record<string, boolean>
  exceptions: ApiException[]
}
interface ExceptionRow { key: string; kind: 'ambassade' | 'direction'; id: number; nom: string; voit_globaux: boolean }


export default /*@__PURE__*/_defineComponent({
  __name: 'DocumentTypeVisibilityPanel',
  setup(__props) {

const toast = useToast()

const portees = [
  { code: 'AMBASSADE', label: 'Utilisateurs rattachés à une ambassade' },
  { code: 'DIRECTION', label: 'Utilisateurs rattachés à une direction' },
  { code: 'NATIONAL', label: 'Utilisateurs sans rattachement (national)' },
]

const loading = ref(true)
const saving = ref(false)
const defaults = reactive<Record<string, boolean>>({ AMBASSADE: true, DIRECTION: true, NATIONAL: true })
const exceptions = ref<ExceptionRow[]>([])
const ambassades = ref<Entity[]>([])
const directions = ref<Entity[]>([])
const newEntity = ref('')

const isTaken = (kind: 'ambassade' | 'direction', id: number) =>
  exceptions.value.some(e => e.kind === kind && e.id === id)
const availableAmbassades = computed(() => ambassades.value.filter(a => !isTaken('ambassade', a.id)))
const availableDirections = computed(() => directions.value.filter(d => !isTaken('direction', d.id)))

const toRows = (list: ApiException[]): ExceptionRow[] =>
  list.map(e => e.ambassade_id
    ? { key: `a${e.ambassade_id}`, kind: 'ambassade', id: e.ambassade_id, nom: e.nom, voit_globaux: e.voit_globaux }
    : { key: `d${e.direction_id}`, kind: 'direction', id: e.direction_id as number, nom: e.nom, voit_globaux: e.voit_globaux })

const apply = (payload: ApiPayload) => {
  Object.assign(defaults, payload.defaults)
  exceptions.value = toRows(payload.exceptions)
}

const addException = () => {
  const [kind, rawId] = newEntity.value.split(':')
  const id = Number(rawId)
  const list = kind === 'a' ? ambassades.value : directions.value
  const entity = list.find(e => e.id === id)
  if (!entity) return
  // Par défaut, l'exception inverse la règle générale de sa portée.
  const portee = kind === 'a' ? 'AMBASSADE' : 'DIRECTION'
  exceptions.value.push({
    key: `${kind}${id}`,
    kind: kind === 'a' ? 'ambassade' : 'direction',
    id,
    nom: entity.nom,
    voit_globaux: !defaults[portee],
  })
  newEntity.value = ''
}

const save = async () => {
  saving.value = true
  try {
    const payload = await apiFetch<ApiPayload>('/api/admin/document-types/visibility/', {
      method: 'PUT',
      body: JSON.stringify({
        defaults,
        exceptions: exceptions.value.map(e => ({
          ...(e.kind === 'ambassade' ? { ambassade_id: e.id } : { direction_id: e.id }),
          voit_globaux: e.voit_globaux,
        })),
      }),
    })
    apply(payload)
    toast.success('Visibilité enregistrée', 'La règle s\'applique dès la prochaine rédaction')
  } catch (e: any) {
    toast.error('Erreur', e.message || 'Impossible d\'enregistrer la visibilité')
  } finally {
    saving.value = false
  }
}

onMounted(async () => {
  try {
    const [payload, amb, dirs] = await Promise.all([
      apiFetch<ApiPayload>('/api/admin/document-types/visibility/'),
      apiFetch<Entity[]>('/api/admin/ambassades/'),
      apiFetch<Entity[]>('/api/admin/directions/'),
    ])
    ambassades.value = amb
    directions.value = dirs
    apply(payload)
  } catch (e: any) {
    toast.error('Erreur', e.message || 'Impossible de charger la visibilité des types de document')
  } finally {
    loading.value = false
  }
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("section", _hoisted_1, [
    _cache[7] || (_cache[7] = _createElementVNode("div", { class: "px-5 py-4 border-b border-gray-100" }, [
      _createElementVNode("h3", { class: "font-semibold text-[#22282F]" }, "Visibilité des types de document"),
      _createElementVNode("p", { class: "text-sm text-gray-500 mt-1" }, [
        _createTextVNode(" Choisissez qui voit les types de document "),
        _createElementVNode("strong", null, "globaux"),
        _createTextVNode(" (non rattachés à une entité) lors de la rédaction d'une note ou d'une réponse à un courrier. Les types propres à l'entité de l'utilisateur restent toujours visibles. ")
      ])
    ], -1)),
    (loading.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, _cache[1] || (_cache[1] = [
          _createElementVNode("div", { class: "w-6 h-6 border-4 border-gray-200 border-t-[#22282F] rounded-full animate-spin" }, null, -1)
        ])))
      : (_openBlock(), _createElementBlock("div", _hoisted_3, [
          _createElementVNode("div", null, [
            _cache[2] || (_cache[2] = _createElementVNode("h4", { class: "text-sm font-semibold text-gray-700 mb-2" }, "Règle générale", -1)),
            _createElementVNode("div", _hoisted_4, [
              (_openBlock(), _createElementBlock(_Fragment, null, _renderList(portees, (p) => {
                return _createElementVNode("label", {
                  key: p.code,
                  class: "flex items-center justify-between gap-4 px-4 py-3 cursor-pointer"
                }, [
                  _createElementVNode("span", _hoisted_5, _toDisplayString(p.label), 1),
                  _createElementVNode("span", _hoisted_6, [
                    _createElementVNode("span", {
                      class: _normalizeClass(["text-xs", defaults[p.code] ? 'text-green-600' : 'text-gray-500'])
                    }, _toDisplayString(defaults[p.code] ? 'Voit les types globaux' : 'Ne voit pas les types globaux'), 3),
                    _withDirectives(_createElementVNode("input", {
                      "onUpdate:modelValue": ($event: any) => ((defaults[p.code]) = $event),
                      type: "checkbox",
                      class: "w-4 h-4 accent-[#22282F]"
                    }, null, 8, _hoisted_7), [
                      [_vModelCheckbox, defaults[p.code]]
                    ])
                  ])
                ])
              }), 64))
            ])
          ]),
          _createElementVNode("div", null, [
            _cache[5] || (_cache[5] = _createElementVNode("h4", { class: "text-sm font-semibold text-gray-700 mb-1" }, "Exceptions", -1)),
            _cache[6] || (_cache[6] = _createElementVNode("p", { class: "text-xs text-gray-500 mb-2" }, "Une exception pour une ambassade ou une direction prime sur la règle générale.", -1)),
            (exceptions.value.length)
              ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(exceptions.value, (exc, i) => {
                    return (_openBlock(), _createElementBlock("div", {
                      key: exc.key,
                      class: "flex items-center gap-3 border border-gray-200 rounded-lg px-4 py-2"
                    }, [
                      _createElementVNode("span", _hoisted_9, [
                        _createElementVNode("span", _hoisted_10, _toDisplayString(exc.kind === 'ambassade' ? 'Ambassade' : 'Direction'), 1),
                        _createTextVNode(" " + _toDisplayString(exc.nom), 1)
                      ]),
                      _createElementVNode("label", _hoisted_11, [
                        _withDirectives(_createElementVNode("input", {
                          "onUpdate:modelValue": ($event: any) => ((exc.voit_globaux) = $event),
                          type: "checkbox",
                          class: "w-4 h-4 accent-[#22282F]"
                        }, null, 8, _hoisted_12), [
                          [_vModelCheckbox, exc.voit_globaux]
                        ]),
                        _cache[3] || (_cache[3] = _createTextVNode(" Voit les types globaux "))
                      ]),
                      _createElementVNode("button", {
                        type: "button",
                        class: "text-gray-400 hover:text-red-600 text-sm px-1",
                        title: "Retirer l'exception",
                        onClick: ($event: any) => (exceptions.value.splice(i, 1))
                      }, "✕", 8, _hoisted_13)
                    ]))
                  }), 128))
                ]))
              : (_openBlock(), _createElementBlock("p", _hoisted_14, "Aucune exception.")),
            _createElementVNode("div", _hoisted_15, [
              _withDirectives(_createElementVNode("select", {
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((newEntity).value = $event)),
                class: "flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:border-[#22282F]"
              }, [
                _cache[4] || (_cache[4] = _createElementVNode("option", { value: "" }, "Ajouter une exception pour…", -1)),
                _createElementVNode("optgroup", _hoisted_16, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(availableAmbassades.value, (a) => {
                    return (_openBlock(), _createElementBlock("option", {
                      key: 'a' + a.id,
                      value: 'a:' + a.id
                    }, _toDisplayString(a.nom), 9, _hoisted_17))
                  }), 128))
                ]),
                _createElementVNode("optgroup", _hoisted_18, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(availableDirections.value, (d) => {
                    return (_openBlock(), _createElementBlock("option", {
                      key: 'd' + d.id,
                      value: 'd:' + d.id
                    }, _toDisplayString(d.nom), 9, _hoisted_19))
                  }), 128))
                ])
              ], 512), [
                [_vModelSelect, newEntity.value]
              ]),
              _createElementVNode("button", {
                type: "button",
                disabled: !newEntity.value,
                class: "px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-40",
                onClick: addException
              }, "Ajouter", 8, _hoisted_20)
            ])
          ]),
          _createElementVNode("div", _hoisted_21, [
            _createElementVNode("button", {
              type: "button",
              disabled: saving.value,
              class: "px-4 py-2 bg-[#22282F] text-white rounded-lg text-sm hover:bg-[#22282F]/90 disabled:opacity-50",
              onClick: save
            }, _toDisplayString(saving.value ? 'Enregistrement…' : 'Enregistrer'), 9, _hoisted_22)
          ])
        ]))
  ]))
}
}

})